import { useQuery } from '@tanstack/react-query';
import Chip from '@ui/Chip';
import { getBidsCountByProductId } from '@util/firestore/bid/bid.service';
import { getPublicUserDoc } from '@util/firestore/users';
import { useSellerCoupons } from '@util/hooks/useSellerCoupons';
import { cn, formatCurrency } from '@util/index';
import { isVehicleCategory, type ProductLight } from 'models/product';
import type { UserDocument } from 'models/user';
import ProductFitment from '../MXProductCard/ProductFitment';
import { ListProductAuctionCountdown } from './ListProductAuctionCountdown';
import { ListProductShippingEstimate } from './ListProductShippingEstimate';

export const ListProductInfo = ({
  product,
  userDoc,
}: {
  product: ProductLight;
  userDoc: UserDocument | null | undefined;
}) => {
  const { data: bidCount } = useQuery({
    queryKey: ['bidCount', product.id],
    queryFn: () => getBidsCountByProductId(product.id),
    enabled: product.is_auction,
  });

  let fullPrice = product.price;
  if (product.variation_pricing?.minPrice) {
    fullPrice = product.variation_pricing.minPrice;
  }

  // if (product.variations?.length) {
  //   const letterSize = product.variations.find((v) => !v.is_number);
  //   const numberSize = product.variations.find((v) => v.is_number);
  //   if (letterSize && numberSize) {
  //     fullPrice = letterSize.price + numberSize.price;
  //   } else if (letterSize) {
  //     fullPrice = letterSize.price;
  //   } else if (numberSize) {
  //     fullPrice = numberSize.price;
  //   }
  // }

  const enableCouponDiscount = !isVehicleCategory(product.category);

  const { data: sellerDoc } = useQuery({
    queryKey: ['sellerDoc', product.seller_id],
    queryFn: () => getPublicUserDoc({ uid: product.seller_id }),
    enabled: !!product.seller_id,
  });

  const { sellerCoupons } = useSellerCoupons({
    seller_id: enableCouponDiscount ? product.seller_id : undefined,
    is_active: true,
    is_private: false,
  });

  const activeCoupon =
    enableCouponDiscount && sellerCoupons
      ? sellerCoupons.find(
          (coupon) => !coupon.product_id && coupon.active && !coupon.buyer_id
        )
      : null;

  const getSalePercent = () => {
    if (!product.on_sale) return 0;
    return Math.round(
      ((Number(product.og_price) - product.price) / Number(product.og_price)) *
        100
    );
  };

  const salePercent = getSalePercent();
  return (
    <div className="zinc-100 flex w-full flex-col gap-2 self-stretch bg-white p-[2rem]">
      <div className="flex w-full grow self-stretch">
        <div className="relative flex w-full flex-col gap-2">
          <div className="flex flex-col gap-[0.4rem]">
            {product.out_of_stock && (
              <span className="line-clamp-1 text-[1.6rem] font-medium text-green-500">
                {/* Sold on {formatTimestamp(product.sold_date, true)} */}
                Sold
              </span>
            )}
            <span className="line-clamp-2 overflow-hidden text-ellipsis text-[1.8rem] font-medium leading-relaxed sm:w-[60rem] sm:text-[2.1rem]">
              {product.title}
            </span>

            {product.is_auction && (
              <div className=" flex w-full items-center justify-between gap-[0.8rem]">
                <Chip text={'Auction'} color={'primary'} />
              </div>
            )}

            {product.is_auction && (
              <ListProductAuctionCountdown product={product} />
            )}

            <div className="flex items-center gap-2">
              <span className="text-[1.6rem] font-medium text-zinc-500 sm:text-[1.8rem]">
                {product.condition}
              </span>

              {!!sellerDoc?.badge_counts.fast_shipper && (
                <div className="hidden items-center gap-2 sm:flex">
                  <div className="h-2 w-2 rounded-full bg-zinc-300" />

                  <span className="text-[1.6rem] font-medium text-zinc-500 sm:text-[1.8rem]">
                    Ships within 3 business days
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="hidden flex-col items-start gap-[0.8rem] sm:flex">
            {product.on_sale && !product.is_auction ? (
              <div className="mt-4 flex flex-col gap-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-[2.5rem] font-semibold text-brand-primary">
                    {formatCurrency(fullPrice)}
                  </span>
                  <span className="text-[1.8rem] text-zinc-500 line-through">
                    ${product.og_price}
                  </span>
                </div>
                <span className="text-[1.8rem] font-bold uppercase text-brand-secondary">
                  {salePercent}% off
                </span>
              </div>
            ) : (
              <span className="mt-4 text-[2.5rem] font-semibold">
                {formatCurrency(fullPrice)}
              </span>
            )}

            {activeCoupon && !product.is_auction && (
              <p className={cn('mt-1 text-[1.8rem] font-medium text-zinc-600')}>
                <span className="text-green-600">
                  {activeCoupon.percent_off
                    ? `${activeCoupon.percent_off}% OFF`
                    : `${formatCurrency(activeCoupon.amount_off ?? 0)} OFF`}
                </span>{' '}
                with coupon code
              </p>
            )}

            {bidCount !== undefined && (
              <span className="whitespace-nowrap text-[1.4rem] font-medium text-gray-500">
                {bidCount !== undefined ? `(${bidCount} bids)` : ''}
              </span>
            )}
          </div>

          <ListProductShippingEstimate product={product} userDoc={userDoc} />

          <div className="flex flex-col items-start gap-[0.8rem] sm:hidden lg:w-[16rem]">
            {product.on_sale && !product.is_auction ? (
              <div className="mt-4 flex flex-col gap-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-[2.1rem] font-semibold text-brand-primary">
                    {formatCurrency(fullPrice)}
                  </span>
                  <span className="text-[1.8rem] text-zinc-500 line-through">
                    ${product.og_price}
                  </span>
                </div>
                <span className="text-[1.8rem] font-bold uppercase text-brand-secondary">
                  {salePercent}% off
                </span>
              </div>
            ) : (
              <span className="mt-4 text-[2.1rem] font-semibold">
                {formatCurrency(fullPrice)}
              </span>
            )}

            {activeCoupon && !product.is_auction && (
              <p className={cn('mt-1 text-[1.4rem] font-medium text-zinc-600')}>
                <span className="text-green-600">
                  {activeCoupon.percent_off
                    ? `${activeCoupon.percent_off}% OFF`
                    : `${formatCurrency(activeCoupon.amount_off ?? 0)} OFF`}
                </span>{' '}
                with coupon code
              </p>
            )}

            {bidCount !== undefined && (
              <span className="whitespace-nowrap text-[1.4rem] font-medium text-gray-500">
                {bidCount !== undefined ? `(${bidCount} bids)` : ''}
              </span>
            )}
          </div>

          <ProductFitment product={product} userDoc={userDoc} />

          {product.sold_count && product.sold_count >= 50 && (
            <span className="text-[1.8rem] font-medium text-brand-secondary">
              {product.sold_count} sold
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
